// Hand-rolled SVG charts: one y-axis each, a crosshair that snaps to the
// nearest day, and a tooltip listing every series at that day.

import { useEffect, useRef, useState } from 'preact/hooks';
import { prettyDate } from '../format.ts';

export interface Series {
  key: string;
  label: string;
  kind: 'bar' | 'line' | 'dots';
  /** CSS class carrying the series colour. */
  cls: string;
  values: (number | null)[];
  format: (v: number) => string;
}

const H = 220;
const PAD = { top: 12, right: 12, bottom: 24, left: 58 };

function useWidth() {
  const ref = useRef<HTMLDivElement>(null);
  const [w, setW] = useState(600);
  useEffect(() => {
    const ro = new ResizeObserver(([e]) => setW(Math.max(280, e.contentRect.width)));
    ro.observe(ref.current!);
    return () => ro.disconnect();
  }, []);
  return [ref, w] as const;
}

function niceTicks(min: number, max: number, count = 4): number[] {
  const span = max - min || 1;
  const raw = span / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw)!;
  const out: number[] = [];
  for (let v = Math.ceil(min / step) * step; v <= max + 1e-9; v += step) out.push(+v.toFixed(6));
  return out;
}

export function Chart({ dates, series, zeroBased, label }: {
  dates: string[];
  series: Series[];
  zeroBased: boolean;
  label: string;
}) {
  const [ref, W] = useWidth();
  const [hover, setHover] = useState<number | null>(null);
  const all = series.flatMap((s) => s.values).filter((v): v is number => v !== null);
  if (!all.length) {
    return <div ref={ref} class="chart empty muted">Not enough data yet.</div>;
  }
  let lo = zeroBased ? 0 : Math.min(...all);
  let hi = Math.max(...all);
  if (!zeroBased) {
    const pad = Math.max(0.5, (hi - lo) * 0.15);
    lo -= pad;
    hi += pad;
  } else hi *= 1.08;
  const ticks = niceTicks(lo, hi);
  lo = Math.min(lo, ticks[0]);
  const iw = W - PAD.left - PAD.right;
  const ih = H - PAD.top - PAD.bottom;
  const n = dates.length;
  const step = iw / n;
  const x = (i: number) => PAD.left + step * (i + 0.5);
  const y = (v: number) => PAD.top + ih - ((v - lo) / (hi - lo)) * ih;
  const barW = Math.max(1, Math.min(14, step - 2));
  const labelEvery = Math.ceil(n / Math.max(2, Math.floor(iw / 70)));

  function move(e: PointerEvent) {
    const r = (e.currentTarget as SVGElement).getBoundingClientRect();
    const i = Math.floor((e.clientX - r.left - PAD.left) / step);
    setHover(i >= 0 && i < n ? i : null);
  }

  return (
    <div ref={ref} class="chart">
      <div class="legend">
        {series.map((s) => (
          <span key={s.key} class={`key k-${s.kind} ${s.cls}`}>
            <i />
            {s.label}
          </span>
        ))}
      </div>
      <svg
        width={W}
        height={H}
        role="img"
        aria-label={label}
        onPointerMove={move}
        onPointerDown={move}
        onPointerLeave={() => setHover(null)}
      >
        {ticks.map((t) => (
          <g key={t} class="grid">
            <line x1={PAD.left} x2={W - PAD.right} y1={y(t)} y2={y(t)} />
            <text x={PAD.left - 6} y={y(t)} dy="0.32em" text-anchor="end">
              {series[0].format(t)}
            </text>
          </g>
        ))}
        {dates.map((d, i) =>
          i % labelEvery === 0 ? (
            <text key={d} class="xlabel" x={x(i)} y={H - 6} text-anchor="middle">
              {new Date(d + 'T12:00:00Z').toLocaleDateString(undefined, { timeZone: 'UTC', month: 'short', day: 'numeric' })}
            </text>
          ) : null,
        )}
        {series.map((s) => {
          if (s.kind === 'bar')
            return (
              <g key={s.key} class={`bars ${s.cls}`}>
                {s.values.map((v, i) =>
                  v === null ? null : (
                    <path
                      key={i}
                      d={roundedTop(x(i) - barW / 2, y(v), barW, y(lo) - y(v), Math.min(4, barW / 2))}
                      opacity={hover === null || hover === i ? 1 : 0.55}
                    />
                  ),
                )}
              </g>
            );
          if (s.kind === 'dots')
            return (
              <g key={s.key} class={`dots ${s.cls}`}>
                {s.values.map((v, i) => (v === null ? null : <circle key={i} cx={x(i)} cy={y(v)} r={hover === i ? 4.5 : 3} />))}
              </g>
            );
          const pts = s.values.map((v, i) => (v === null ? null : `${x(i).toFixed(1)},${y(v).toFixed(1)}`));
          const segs: string[][] = [[]];
          for (const p of pts) p ? segs[segs.length - 1].push(p) : segs.push([]);
          return (
            <g key={s.key} class={`line ${s.cls}`}>
              {segs.filter((g) => g.length > 1).map((g, k) => <polyline key={k} points={g.join(' ')} />)}
              {hover !== null && s.values[hover] !== null && <circle cx={x(hover)} cy={y(s.values[hover]!)} r={4} />}
            </g>
          );
        })}
        {hover !== null && <line class="crosshair" x1={x(hover)} x2={x(hover)} y1={PAD.top} y2={PAD.top + ih} />}
      </svg>
      {hover !== null && (
        <div class="tooltip" style={{ left: `${Math.min(W - 150, Math.max(0, x(hover) - 75))}px` }}>
          <div class="muted">{prettyDate(dates[hover])}</div>
          {series.map((s) => (
            <div key={s.key}>
              <strong>{s.values[hover] === null ? '—' : s.format(s.values[hover]!)}</strong> {s.label.toLowerCase()}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** A bar with its data end rounded and its baseline end square. */
function roundedTop(x: number, y: number, w: number, h: number, r: number): string {
  if (h <= 0) return '';
  r = Math.min(r, h);
  return `M${x},${y + h}V${y + r}Q${x},${y} ${x + r},${y}H${x + w - r}Q${x + w},${y} ${x + w},${y + r}V${y + h}Z`;
}
